import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Home from './pages/Home';
import Imprint from './pages/Imprint';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { LanguageProvider, useLanguage } from './context/LanguageContext';

function AppContent() {
  const { t, lang } = useLanguage();

  useEffect(() => {
    // 1. Update Document Title & Meta Description
    document.title = t.seo.title;
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = t.seo.description;

    // 2. Update Open Graph & Twitter Tags
    const updateMetaTag = (attr, value, content) => {
      let tag = document.querySelector(`meta[${attr}="${value}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, value);
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    updateMetaTag('property', 'og:title', t.seo.title);
    updateMetaTag('property', 'og:description', t.seo.description);
    updateMetaTag('property', 'og:url', window.location.href);
    updateMetaTag('name', 'twitter:title', t.seo.title);
    updateMetaTag('name', 'twitter:description', t.seo.description);
    updateMetaTag('name', 'twitter:card', 'summary_large_image');

    // 3. i18n Hreflang Tags
    const updateHreflang = (langCode, href) => {
      let link = document.querySelector(`link[hreflang="${langCode}"]`);
      if (!link) {
        link = document.createElement('link');
        link.rel = 'alternate';
        link.hreflang = langCode;
        document.head.appendChild(link);
      }
      link.href = href;
    };
    
    // Assuming root for both as it's a single page app with state-based lang
    updateHreflang('en', 'https://metanoia.beer/');
    updateHreflang('es', 'https://metanoia.beer/');
    updateHreflang('x-default', 'https://metanoia.beer/');

    // 4. JSON-LD Structured Data for AI & Search Engines
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Brewery",
          "@id": "https://metanoia.beer/#brewery",
          "name": "METANOIA",
          "description": "Ultra-exclusive, hand-brewed premium beer. Allocation only.",
          "url": "https://metanoia.beer/",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Barberà del Vallès",
            "addressRegion": "Barcelona",
            "addressCountry": "ES"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "41.5177",
            "longitude": "2.1276"
          }
        },
        {
          "@type": "Product",
          "name": "METANOIA Batch #1",
          "description": "Ultra-exclusive, hand-brewed premium beer. Limited numbered first edition.",
          "brand": {
            "@type": "Brand",
            "name": "METANOIA"
          },
          "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/LimitedAvailability",
            "description": "Limited Edition, strictly 100 bottles.",
            "areaServed": "ES"
          }
        }
      ]
    };

    let scriptTag = document.getElementById('json-ld');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = 'json-ld';
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    scriptTag.text = JSON.stringify(structuredData);

    // Update HTML lang attribute
    document.documentElement.lang = lang;
  }, [t, lang]);

  return (
    <main className="min-h-screen bg-brand-black" role="main">
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/imprint" element={<Imprint />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
      </Routes>
    </main>
  );
}

function App() {
  return (
    <LanguageProvider>
      <Router>
        <AppContent />
      </Router>
    </LanguageProvider>
  );
}

export default App;